import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/utils";
import BrandLockup from "./BrandLockup";
import ThemeToggle from "@/components/shared/ThemeToggle";
import ConnectionIndicator from "@/components/shared/ConnectionIndicator";
import type { ConnectionState } from "@/lib/socket";

const navLinks = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/trips/new", label: "New Trip" },
  { to: "/events", label: "Meetings" },
  { to: "/bookings", label: "Bookings" },
  { to: "/travelers", label: "Travelers" },
  { to: "/settings", label: "Settings" },
];

interface NavbarProps {
  connectionState: ConnectionState;
}

export default function Navbar({ connectionState }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate("/");
  }

  return (
    <nav className="sticky top-0 z-40 w-full border-b border-border bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/70">
      <div className="container mx-auto flex h-14 max-w-7xl items-center px-4">
        <NavLink to="/dashboard" className="mr-6 flex items-center transition-opacity hover:opacity-80">
          <BrandLockup tagline="Everyone's Fare Search Coach" />
        </NavLink>

        <div className="hidden md:flex items-center gap-1 flex-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <ConnectionIndicator state={connectionState} />
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>

        <div className="flex md:hidden items-center gap-2 ml-auto">
          <ConnectionIndicator state={connectionState} />
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card px-4 py-3 space-y-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "block px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            Logout
          </button>
        </div>
      )}
    </nav>
  );
}
